import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { CompanyData } from '@/utils/companyUtils';
import { trackAccountCreated, trackMemberJoined, setAmplitudeUserId } from '@/utils/amplitudeEvents';
import { getAttribution } from '@/utils/marketingAttribution';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, companyData?: CompanyData) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  userRoles: string[];
  companyRole: string | null; // 'owner' | 'admin' | 'member' | null
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  isCompanyOwner: () => boolean;
  checkAdminStatus: () => Promise<void>;
  associateWithCompany: (companyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [companyRole, setCompanyRole] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Function to process stored plan after login
  const processStoredPlan = useCallback(async () => {
    const storedPlan = sessionStorage.getItem('selectedPlan');
    if (!storedPlan) return;

    try {
      const planInfo = JSON.parse(storedPlan);
      console.log('AuthContext: Processing stored plan after login:', planInfo);
      
      sessionStorage.removeItem('selectedPlan');
      
      setTimeout(async () => {
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          
          if (!currentSession?.access_token) {
            console.error('AuthContext: No valid session for stored plan processing');
            navigate('/dashboard');
            return;
          }

          const { data, error } = await supabase.functions.invoke('create-checkout', {
            body: { 
              priceId: planInfo.priceId, 
              planName: planInfo.planName || planInfo.name 
            },
            headers: {
              Authorization: `Bearer ${currentSession.access_token}`,
            },
          });

          if (error) {
            console.error('AuthContext: Error processing stored plan:', error);
            toast({
              title: "Checkout Error",
              description: "Failed to process your plan selection. Please try again.",
              variant: "destructive"
            });
            navigate('/dashboard');
            return;
          }

          if (data?.url) {
            console.log('AuthContext: Redirecting to Stripe checkout for stored plan');
            toast({
              title: "Redirecting to Checkout",
              description: "Processing your Pro subscription...",
            });
            
            const stripeWindow = window.open(data.url, '_blank');
            if (!stripeWindow) {
              window.location.href = data.url;
            }
          } else {
            console.error('AuthContext: No checkout URL received');
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('AuthContext: Error in stored plan processing:', error);
          navigate('/dashboard');
        }
      }, 1000);
      
    } catch (error) {
      console.error('AuthContext: Error parsing stored plan:', error);
      sessionStorage.removeItem('selectedPlan');
      navigate('/dashboard');
    }
  }, [navigate, toast]);

  // Function to check for invitations and auto-associate with company
  const checkInvitationsAndAssociate = useCallback(async (user: User) => {
    try {
      console.log('AuthContext: Checking for invitations for user:', user.email);
      
      const { data: invitations, error: invitationError } = await supabase
        .from('company_invitations')
        .select('*, companies(name)')
        .eq('email', user.email)
        .is('used_at', null)
        .order('created_at', { ascending: false });

      if (invitationError) {
        console.error('AuthContext: Error checking invitations:', invitationError);
        return;
      }

      if (invitations && invitations.length > 0) {
        const invitation = invitations[0];
        console.log('AuthContext: Found invitation for user, associating with company:', invitation.company_id);

        const { error: associateError } = await supabase.rpc('accept_company_invitation', {
          p_user_id: user.id,
          p_company_id: invitation.company_id
        });

        if (associateError) {
          console.error('AuthContext: Error associating user with company:', associateError);
          return;
        }

        const { error: updateError } = await supabase
          .from('company_invitations')
          .update({ used_at: new Date().toISOString() })
          .eq('id', invitation.id);

        if (updateError) {
          console.error('AuthContext: Error marking invitation as used:', updateError);
        }

        console.log('AuthContext: Successfully associated user with company via invitation');
        
        // EVENT 6: Member Joined
        trackMemberJoined({
          company_id: invitation.company_id,
          role: 'member',
          invitation_id: invitation.id,
        });

        toast({
          title: "Welcome!",
          description: `You've been added to ${invitation.companies?.name || 'your company'}.`,
        });
      } else {
        console.log('AuthContext: No valid invitations found for user, ProtectedRoute will handle redirect to onboarding');
      }
    } catch (error) {
      console.error('AuthContext: Error in invitation checking:', error);
      // ProtectedRoute will handle redirect to /onboarding/company
    }
  }, [toast]);


  const checkAdminStatus = useCallback(async () => {
    if (!user?.id) {
      console.log('AuthContext: No user ID available for admin status check');
      setUserRoles([]);
      setCompanyRole(null);
      return;
    }

    try {
      console.log('AuthContext: Checking admin status for user:', user.id);
      
      // Fetch platform-level roles from user_roles (only super_admin now)
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (rolesError) {
        console.error("AuthContext: Error fetching user roles:", rolesError.message);
        setUserRoles([]);
      } else {
        const roles = rolesData?.map(item => item.role) || [];
        console.log('AuthContext: Platform roles fetched:', roles);
        setUserRoles(roles);
      }

      // Fetch company-level role using the new RPC function
      const { data: companyRoleData, error: companyRoleError } = await supabase
        .rpc('get_user_company_role', { _user_id: user.id });

      if (companyRoleError) {
        console.error("AuthContext: Error fetching company role:", companyRoleError.message);
        setCompanyRole(null);
      } else {
        console.log('AuthContext: Company role fetched:', companyRoleData);
        setCompanyRole(companyRoleData);
      }
      
      console.log('AuthContext: Admin status check completed successfully');
    } catch (error: any) {
      console.error("AuthContext: Error checking admin status:", error.message);
      setUserRoles([]);
      setCompanyRole(null);
    }
  }, [user?.id]);

  // Use refs to avoid re-subscribing the auth listener when callbacks change
  const checkAdminStatusRef = React.useRef(checkAdminStatus);
  const processStoredPlanRef = React.useRef(processStoredPlan);
  const checkInvitationsRef = React.useRef(checkInvitationsAndAssociate);
  
  useEffect(() => { checkAdminStatusRef.current = checkAdminStatus; }, [checkAdminStatus]);
  useEffect(() => { processStoredPlanRef.current = processStoredPlan; }, [processStoredPlan]);
  useEffect(() => { checkInvitationsRef.current = checkInvitationsAndAssociate; }, [checkInvitationsAndAssociate]);

  useEffect(() => {
    console.log('AuthContext: Initializing auth state');
    
    let initialized = false;
    
    // Set up auth state listener FIRST - runs only once
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('AuthContext: Auth state changed', { 
        event, 
        hasSession: !!session, 
        hasUser: !!session?.user,
        userId: session?.user?.id 
      });
      
      // Skip TOKEN_REFRESHED events - they don't change user state
      if (event === 'TOKEN_REFRESHED') {
        console.log('AuthContext: Token refreshed, skipping state update');
        setSession(session);
        return;
      }
      
      // Use functional updater to prevent new reference if user ID hasn't changed
      // This stops cascading re-renders in CompanyContext/SubscriptionContext on tab return
      setUser(prev => {
        if (prev?.id === session?.user?.id) return prev; // Same user, keep stable reference
        return session?.user ?? null;
      });
      setSession(session);
      
      if (!session?.user) {
        console.log('AuthContext: Clearing user roles (preserving anonymous session data)');
        setUserRoles([]);
        if (initialized) {
          setLoading(false);
        }
      } else {
        console.log('AuthContext: Loading user roles for authenticated user');
        
        // Set Amplitude user ID on every auth state change
        setAmplitudeUserId(session.user.id);
        
        // Preserve existing session data when updating
        const existingData = localStorage.getItem('userSessionData');
        let existingSession = {};
        try {
          if (existingData) existingSession = JSON.parse(existingData);
        } catch (err) {
          console.warn('Failed to parse existing session data:', err);
        }
        
        localStorage.setItem('userSessionData', JSON.stringify({
          ...existingSession,
          userId: session.user.id,
          email: session.user.email,
          timestamp: Date.now()
        }));
        
        // Load roles immediately after setting user
        setTimeout(async () => {
          try {
            await checkAdminStatusRef.current();
          } catch (error) {
            console.error('AuthContext: Error loading user roles:', error);
          } finally {
            if (initialized) {
              setLoading(false);
            }
          }
        }, 100);
        
        if (event === 'SIGNED_IN') {
          console.log('AuthContext: User signed in, checking for stored plan');
          processStoredPlanRef.current();
          
          setTimeout(() => {
            checkInvitationsRef.current(session.user);
          }, 2000);
        }
      }
    });

    // Get initial session AFTER setting up listener
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('AuthContext: Error getting initial session:', error);
          setLoading(false);
          return;
        }
        
        console.log('AuthContext: Initial session check', { 
          hasSession: !!session, 
          hasUser: !!session?.user,
          userId: session?.user?.id 
        });
        
        if (session) {
          setUser(session.user);
          setSession(session);
          setAmplitudeUserId(session.user.id);
          
          if (session.user) {
            const existingData = localStorage.getItem('userSessionData');
            let existingSession = {};
            try {
              if (existingData) existingSession = JSON.parse(existingData);
            } catch (err) {
              console.warn('Failed to parse existing session data:', err);
            }
            
            localStorage.setItem('userSessionData', JSON.stringify({
              ...existingSession,
              userId: session.user.id,
              email: session.user.email,
              timestamp: Date.now()
            }));
            
            await checkAdminStatusRef.current();
          }
        }
        
        initialized = true;
        setLoading(false);
      } catch (error) {
        console.error('AuthContext: Error in initial session check:', error);
        initialized = true;
        setLoading(false);
      }
    };

    getInitialSession();

    return () => {
      console.log('AuthContext: Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []); // Empty deps - listener set up once, uses refs for latest callbacks

  const signUp = async (email: string, password: string, companyData?: CompanyData) => {
    try {
      console.log('AuthContext: Starting signup process', { 
        hasCompanyData: !!companyData,
        companyName: companyData?.companyName 
      });
      setLoading(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: 'https://www.zentrixinsights.com/auth/callback',
        },
      });
      
      if (error) {
        console.error('AuthContext: Signup error', error);
        return { error };
      }
      
      console.log('AuthContext: Signup successful', { 
        hasUser: !!data.user, 
        hasSession: !!data.session,
        userId: data.user?.id 
      });

      // EVENT 1: Account Created (fallback for flows using AuthContext.signUp)
      if (data.user) {
        const attribution = getAttribution();
        trackAccountCreated({
          method: 'email',
          account_type: 'owner',
          referrer: document.referrer || undefined,
          utm_source: attribution.utm_source || undefined,
          utm_medium: attribution.utm_medium || undefined,
          utm_campaign: attribution.utm_campaign || undefined,
        });

        // Persist attribution to database
        supabase.rpc('insert_user_attribution', {
          p_utm_source: attribution.utm_source || null,
          p_utm_medium: attribution.utm_medium || null,
          p_utm_campaign: attribution.utm_campaign || null,
          p_utm_adset: attribution.utm_adset || null,
          p_utm_ad: attribution.utm_ad || null,
          p_utm_content: attribution.utm_content || null,
          p_gclid: attribution.gclid || null,
          p_fbclid: attribution.fbclid || null,
          p_li_fat_id: attribution.li_fat_id || null,
          p_landing_page_url: attribution.landing_page_url || null,
          p_first_seen_at: attribution.first_seen_at || null,
        }).then(({ error }) => {
          if (error) console.error('Attribution insert error:', error);
        });
      }

      return { error: null };
    } catch (error: any) {
      console.error("AuthContext: Error signing up:", error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Starting signin process');
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('AuthContext: Signin error', error);
        return { error };
      }
      
      console.log('AuthContext: Signin successful', { 
        hasUser: !!data.user, 
        hasSession: !!data.session,
        userId: data.user?.id 
      });
      
      return { error: null };
    } catch (error: any) {
      console.error("AuthContext: Error signing in:", error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('AuthContext: Starting signout process');
      setLoading(true);
      
      console.log('AuthContext: Clearing application storage');
      localStorage.removeItem('userSessionData');
      localStorage.removeItem('preferredLanguage');
      sessionStorage.removeItem('selectedPlan');
      setUser(null);
      setSession(null);
      setUserRoles([]);
      
      console.log('AuthContext: Signing out from Supabase');
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) {
        console.error('AuthContext: Error during signout:', error);
      }
      
      console.log('AuthContext: Signout successful, navigating to home');
      
      navigate('/', { replace: true });
      
    } catch (error: any) {
      console.error("AuthContext: Error signing out:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email },
      });
      
      if (error) {
        return { error };
      }
      
      return { error: null };
    } catch (error: any) {
      console.error("Error resetting password:", error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        return { error };
      }
      
      return { error: null };
    } catch (error: any) {
      console.error("Error updating password:", error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const associateWithCompany = useCallback(async (companyId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.rpc('associate_user_with_company', {
        p_user_id: user.id,
        p_company_id: companyId
      });

      if (error) throw error;

      await checkAdminStatus();

      console.log('User successfully associated with company');
    } catch (error: any) {
      console.error('Error associating user with company:', error);
      throw error;
    }
  }, [user, checkAdminStatus]);

  const isSuperAdmin = useCallback((): boolean => {
    return userRoles.includes('super_admin');
  }, [userRoles]);

  // Company owner - highest company-level role
  const isCompanyOwner = useCallback((): boolean => {
    return companyRole === 'owner';
  }, [companyRole]);

  // Company admin - owner or admin role in company
  const isCompanyAdmin = useCallback((): boolean => {
    return companyRole === 'owner' || companyRole === 'admin';
  }, [companyRole]);

  // General admin check - super_admin OR company owner/admin
  const isAdmin = useCallback((): boolean => {
    return userRoles.includes('super_admin') || companyRole === 'owner' || companyRole === 'admin';
  }, [userRoles, companyRole]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    userRoles,
    companyRole,
    isAdmin,
    isSuperAdmin,
    isCompanyAdmin,
    isCompanyOwner,
    checkAdminStatus,
    associateWithCompany
  }), [user, session, loading, userRoles, companyRole, isAdmin, isSuperAdmin, isCompanyAdmin, isCompanyOwner, checkAdminStatus, associateWithCompany]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AUTH_CONTEXT_DEFAULT: AuthContextType = {
  user: null,
  session: null,
  loading: false,
  signUp: async () => ({ error: new Error('No AuthProvider') }),
  signIn: async () => ({ error: new Error('No AuthProvider') }),
  signOut: async () => {},
  resetPassword: async () => ({ error: new Error('No AuthProvider') }),
  updatePassword: async () => ({ error: new Error('No AuthProvider') }),
  userRoles: [],
  companyRole: null,
  isAdmin: () => false,
  isSuperAdmin: () => false,
  isCompanyAdmin: () => false,
  isCompanyOwner: () => false,
  checkAdminStatus: async () => {},
  associateWithCompany: async () => {},
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context ?? AUTH_CONTEXT_DEFAULT;
};
